import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';

interface FAQItem { question: string; answer: string; category: string; }

export default function FaqPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      category: 'Adoption & Welfare',
      question: 'How does the adoption process work?',
      answer: 'Once you select a companion and complete the checkout, our adoption counselor will reach out to verify space suitability. After validation, we finalize paperwork and schedule climate-controlled transit directly to your door.'
    },
    {
      category: 'Adoption & Welfare',
      question: 'Are the pets healthy and vaccinated?',
      answer: 'Absolutely. Every pet listed on Royal Pet Store receives a thorough veterinary checkup, complete deworming, microchipping, and appropriate vaccine doses. A complete certified medical file is handed over at the time of delivery.'
    },
    {
      category: 'Product & Feed',
      question: 'Is the pet food organic and safe?',
      answer: 'Yes, we only stock premium, certified organic pet feeds and veterinarian-recommended diets. Each product page lists detailed ingredient profiles, age recommendations, and flavor breakdowns to match your pet’s exact nutrition needs.'
    },
    {
      category: 'Shipping & Transport',
      question: 'How do you transport live animals safely?',
      answer: 'We utilize custom, temperature-regulated vans equipped with clean crates, fresh water, and on-board animal handlers. This ensures a low-stress, climate-controlled environment for your companion throughout their journey.'
    },
    {
      category: 'Shipping & Transport',
      question: 'What are your delivery fees?',
      answer: 'We offer free delivery across Pakistan on all orders above Rs. 10,000. For orders below Rs. 10,000, a shipping fee of Rs. 250 applies.'
    },
    {
      category: 'Policies & Cancellations',
      question: 'Can I return an adopted companion?',
      answer: 'To protect the mental and physical welfare of our animals, adoptions are final and non-returnable. However, we offer 24/7 post-adoption counselor support and a network of veterinary clinics to help your new friend transition smoothly.'
    },
    {
      category: 'Policies & Cancellations',
      question: 'Can I return pet feeds or accessories?',
      answer: 'Yes. For food bags, toys, and bedding, we offer a hassle-free 7-day return policy. Items must be completely unopened, unused, and in their original packaging to qualify.'
    }
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 md:py-16">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs text-gray-500 mb-8">
        <Link to="/" className="hover:text-gray-900 transition-colors">Home</Link>
        <ChevronRight size={12} />
        <span className="text-gray-900 font-medium">FAQs</span>
      </nav>

      {/* Header */}
      <div className="text-center mb-12">
        <div className="w-12 h-12 bg-gray-50 border border-accent rounded-full flex items-center justify-center mx-auto mb-4">
          <HelpCircle size={20} className="text-accent" />
        </div>
        <h1 className="text-3xl md:text-4xl font-serif font-black text-primary tracking-tight">Frequently Asked Questions</h1>
        <div className="w-16 h-0.5 bg-accent mx-auto mt-4 mb-2" />
        <p className="text-gray-500 text-sm max-w-lg mx-auto">
          Find answers to common questions about pet adoption, nutrition feeds, shipping schedules, and store policies.
        </p>
      </div>

      {/* FAQs List */}
      <div className="space-y-4 mb-16">
        {faqs.map((faq, index) => {
          const isOpen = openIndex === index;
          return (
            <div
              key={index}
              className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:border-gray-200 transition-all"
            >
              <button
                onClick={() => setOpenIndex(isOpen ? null : index)}
                className="w-full flex items-center justify-between px-6 py-5 text-left transition-colors hover:bg-gray-50"
              >
                <div>
                  <span className="text-[10px] font-bold text-accent uppercase tracking-wider block mb-1">
                    {faq.category}
                  </span>
                  <h3 className="font-serif font-bold text-primary text-sm sm:text-base leading-snug">
                    {faq.question}
                  </h3>
                </div>
                <div className="text-gray-400 flex-shrink-0 ml-4">
                  {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </div>
              </button>
              
              {isOpen && (
                <div className="px-6 pb-5 pt-1 text-sm text-gray-600 leading-relaxed border-t border-gray-50 bg-[#FAF8F5]/30">
                  <p>{faq.answer}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Support box */}
      <div className="bg-[#FAF8F5] border border-accent/15 rounded-2xl p-8 text-center">
        <h2 className="text-lg font-serif font-bold text-primary mb-2">Still Have Questions?</h2>
        <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
          Our customer support team is active 9:00 AM - 6:00 PM (Mon-Sat) to assist you with order processing, returns, or health certifications.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="https://wa.me/923295025172"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-accent hover:bg-accent/80 text-white font-bold uppercase tracking-wider text-xs px-8 py-3.5 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <span>💬 Chat on WhatsApp</span>
          </a>
          <Link
            to="/contact"
            className="border border-primary text-primary hover:bg-primary hover:text-white font-bold uppercase tracking-wider text-xs px-8 py-3.5 rounded-lg transition-all"
          >
            Contact Form
          </Link>
        </div>
      </div>
    </div>
  );
}
